import React from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import { useAuth } from "../context/AuthProvider";
import AppFooter from "../components/AppFooter";

export default function TermsConditions() {
  const navigate = useNavigate();
  const { session } = useAuth();

  return (
    <div className="min-h-screen bg-[#11111f] text-white font-sans antialiased">
      {/* =====================================================
          MAIN WEBSITE HEADER
      ===================================================== */}
      <header className="sticky top-0 z-50 border-b border-slate-800 bg-[#11111f]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-4">
            {/* LANDING-STYLE LOGO / BRAND */}
            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate("/landing")}
                className="flex items-center gap-3 cursor-pointer min-w-0 text-left"
                aria-label="Go to Reignite Spirit home page"
              >
                <img
                  src={logo}
                  alt="Reignite Spirit Logo"
                  className="w-10 sm:w-12 h-10 sm:h-12 drop-shadow-[0_0_14px_#eb6fbd] flex-shrink-0"
                  width="48"
                  height="48"
                  loading="eager"
                  decoding="async"
                />

                <div className="min-w-[190px]">
                  <h1 className="text-base sm:text-lg font-semibold tracking-wide text-[#EB6AB5] whitespace-nowrap">
                    REIGNITE.SPIRIT
                  </h1>

                  <p className="text-[10px] sm:text-xs text-[#EB6AB5]/80 tracking-[0.15em] uppercase whitespace-nowrap">
                    Rekindle Purpose
                  </p>
                </div>
              </button>
            </div>

            {/* DESKTOP NAVIGATION */}
            <nav className="hidden lg:flex items-center gap-5 text-sm font-sans">
              <Link
                to="/landing"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Home
              </Link>

              <Link
                to="/snapshot"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Snapshot
              </Link>

              <Link
                to="/deep-audit"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Deep Audit
              </Link>

              <Link
                to="/blog"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                Career Intelligence
              </Link>

              <Link
                to="/faq"
                className="text-white/80 hover:text-[#EB6AB5] transition"
              >
                FAQ
              </Link>

              {session ? (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition"
                  >
                    Login
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/register")}
                    className="px-3 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition whitespace-nowrap"
                  >
                    Register
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/choose-plan")}
                    className="px-3 py-2 rounded-lg bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold transition whitespace-nowrap"
                  >
                    Get My Audit
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* =====================================================
          TERMS CONTENT
      ===================================================== */}
      <main className="px-6 py-10">
        <div className="max-w-4xl mx-auto bg-[#141428] border border-[#EB6AB5]/20 rounded-2xl p-6 sm:p-10 shadow-lg space-y-8">
          <h1 className="text-3xl font-bold text-[#EB6AB5] text-center">
            Terms & Conditions of Service
          </h1>

          <p className="text-gray-300 text-sm text-center">
            <strong className="text-[#EB6AB5]">Last Updated:</strong>{" "}
            December 12, 2025
          </p>

          <p className="text-gray-300 text-sm leading-relaxed">
            <strong className="text-[#EB6AB5]">
              IMPORTANT – PLEASE READ CAREFULLY:
            </strong>{" "}
            By clicking "I Agree," purchasing a report, or using the platform
            operated by Reignite.Spirit Ltd (the "Service"), you ("You" or
            "User") agree to be bound by these Terms and Conditions ("Terms").
            If you do not agree to these Terms, you must not use this Service.
          </p>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              1. Nature of the Service (AI Disclaimer)
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Artificial Intelligence:</strong> You acknowledge that
                the Service utilizes advanced AI and Large Language Models
                (LLMs) to generate career strategies.
              </li>

              <li>
                <strong>No Human Review:</strong> Reports are generated
                automatically based on your inputs and are not reviewed by human
                counselors, recruiters, or legal experts.
              </li>

              <li>
                <strong>Possibility of Error ("Hallucinations"):</strong> AI may
                occasionally generate incorrect or fabricated information. You
                agree to verify any information provided before taking action.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              2. Dispute of Accuracy & Right to Cure
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>7-Day Review Period:</strong> If you discover any
                material factual error, you must notify us in writing within
                seven (7) calendar days of report delivery.
              </li>

              <li>
                <strong>Verification & Remedy:</strong> If we determine the
                report contained materially false or inaccurate content, our
                sole obligation is to:
              </li>
            </ul>

            <div className="ml-10 mt-3 text-gray-300 text-sm space-y-2">
              <p>
                (a) Generate one (1) replacement report at no additional cost;
                and
              </p>

              <p>
                (b) Correct the disputed information before resending.
              </p>
            </div>

            <p className="text-gray-300 text-sm leading-relaxed mt-4">
              <strong>Waiver:</strong> Failure to notify us within the 7-day
              period constitutes acceptance of the report, and you waive any
              further right to dispute it.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              3. No Guarantees or Warranties
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>No Employment Guarantee:</strong> We do not guarantee
                job placement, interviews, or career success.
              </li>

              <li>
                <strong>"As Is" Basis:</strong> The Service is provided “as is”
                without warranties of any kind.
              </li>

              <li>
                <strong>Not Professional Advice:</strong> Content is not
                financial, legal, or recruitment advice. You assume full
                responsibility for your decisions.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              4. Payments, Refunds & Data Security
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Digital Goods:</strong> The Career Pivot Report is a
                non-tangible digital product.
              </li>

              <li>
                <strong>No Refunds:</strong> Once the report has been delivered,
                all sales are final (except Clause 2.2 replacement report
                remedy).
              </li>

              <li>
                <strong>Payment Gateway:</strong> Payments are handled by
                third-party processors. We do not store or transmit your credit
                card/bank data. We only receive a confirmation token to trigger
                report generation.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              5. Limitation of Liability
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Liability Cap:</strong> Our maximum liability will never
                exceed the amount you paid for the Service.
              </li>

              <li>
                <strong>Exclusion of Damages:</strong> We are not liable for
                indirect, incidental, or consequential damages including lost
                wages or opportunities.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              6. Intellectual Property
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Ownership:</strong> All AI systems, prompts, and
                methodologies remain the property of Reignite.Spirit Ltd.
              </li>

              <li>
                <strong>License:</strong> You are granted a limited license to
                use your report for personal, non-commercial purposes.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              7. Data Privacy & Consent
            </h2>

            <p className="text-gray-300 text-sm leading-relaxed mt-2">
              By using the Service, you consent to processing of your personal
              data in accordance with our Privacy Policy. Your data may be
              processed by providers such as Google Cloud, OpenAI, Stripe, and
              Supabase.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#EB6AB5]">
              8. Governing Law and Jurisdiction
            </h2>

            <ul className="list-disc ml-6 mt-3 text-gray-300 text-sm space-y-2">
              <li>
                <strong>Governing Law:</strong> These Terms are governed by the
                laws of the Abu Dhabi Global Market (ADGM).
              </li>

              <li>
                <strong>Jurisdiction:</strong> All disputes shall be resolved
                exclusively in the ADGM Courts in Abu Dhabi, UAE.
              </li>
            </ul>
          </div>
        </div>
      </main>
      <AppFooter
  variant="landing"
  logoSrc={logo}
/>
    </div>
  );
}